import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';

const AppLockContext = createContext(null);

export const useAppLock = () => {
  const context = useContext(AppLockContext);
  if (!context) {
    throw new Error('useAppLock must be used within an AppLockProvider');
  }
  return context;
};

// Settings keys
const APP_LOCK_ENABLED_KEY = 'app_lock_enabled';
const LOCK_ON_BACKGROUND_KEY = 'lock_on_background';
const LOCK_TIMEOUT_KEY = 'lock_timeout_ms';
const REQUIRE_FOR_SENSITIVE_KEY = 'require_unlock_for_sensitive';
const LAST_ACTIVE_KEY = 'app_last_active';
const LAST_SENSITIVE_UNLOCK_KEY = 'last_sensitive_unlock';

// Timeout options (in milliseconds)
export const LOCK_TIMEOUT_OPTIONS = [
  { value: 0, label: 'Immediately', description: 'Lock as soon as you leave' },
  { value: 30000, label: '30 seconds', description: 'Quick lock after brief absence' },
  { value: 60000, label: '1 minute', description: 'Short break friendly' },
  { value: 300000, label: '5 minutes', description: 'Moderate security' },
  { value: 900000, label: '15 minutes', description: 'Relaxed security' },
  { value: -1, label: 'Never', description: 'Only lock on app close' },
];

const DEFAULT_LOCK_TIMEOUT = 30000; // 30 seconds default
const SENSITIVE_UNLOCK_DURATION = 60000; // 1 minute - how long sensitive unlock is valid

export const AppLockProvider = ({ children }) => {
  const { isAuthenticated, user } = useAuth();
  const [isLocked, setIsLocked] = useState(false);
  const [appLockEnabled, setAppLockEnabled] = useState(() => {
    return localStorage.getItem(APP_LOCK_ENABLED_KEY) === 'true';
  });
  const [lockOnBackground, setLockOnBackground] = useState(() => {
    return localStorage.getItem(LOCK_ON_BACKGROUND_KEY) !== 'false'; // Default true
  });
  const [lockTimeout, setLockTimeout] = useState(() => {
    const stored = localStorage.getItem(LOCK_TIMEOUT_KEY);
    return stored !== null ? parseInt(stored, 10) : DEFAULT_LOCK_TIMEOUT;
  });
  const [requireForSensitive, setRequireForSensitive] = useState(() => {
    return localStorage.getItem(REQUIRE_FOR_SENSITIVE_KEY) !== 'false'; // Default true
  });
  const [checkingLock, setCheckingLock] = useState(true);
  const [pendingSensitiveAction, setPendingSensitiveAction] = useState(null);
  const lastActiveRef = useRef(Date.now());
  const visibilityTimeoutRef = useRef(null);

  // Get effective timeout (handle -1 for "Never")
  const getEffectiveTimeout = useCallback(() => {
    return lockTimeout === -1 ? Infinity : lockTimeout;
  }, [lockTimeout]);

  // Helper to check if demo mode is active (from localStorage)
  const isDemoModeActive = () => localStorage.getItem('forgetmenot_demo_mode') === 'true';

  // Check if app should be locked on initial load
  useEffect(() => {
    // In demo mode, skip all lock checking
    if (isDemoModeActive()) {
      setIsLocked(false);
      setCheckingLock(false);
      return;
    }

    if (!isAuthenticated) {
      setIsLocked(false);
      setCheckingLock(false);
      return;
    }

    if (!appLockEnabled) {
      setIsLocked(false);
      setCheckingLock(false);
      return;
    }

    // Check if we need to show lock screen
    const lastActive = parseInt(localStorage.getItem(LAST_ACTIVE_KEY) || '0', 10);
    const timeSinceActive = Date.now() - lastActive;
    const effectiveTimeout = getEffectiveTimeout();
    
    // If app was closed for more than the timeout, require unlock
    if (effectiveTimeout !== Infinity && timeSinceActive > effectiveTimeout) {
      setIsLocked(true);
    } else {
      setIsLocked(false);
    }
    
    setCheckingLock(false);
  }, [isAuthenticated, appLockEnabled, getEffectiveTimeout]);

  // Track visibility changes (app backgrounding)
  useEffect(() => {
    if (!isAuthenticated || !appLockEnabled || !lockOnBackground) return;

    const effectiveTimeout = getEffectiveTimeout();
    if (effectiveTimeout === Infinity) return; // "Never" timeout, don't auto-lock

    const handleVisibilityChange = () => {
      if (document.hidden) {
        // App going to background - record timestamp
        localStorage.setItem(LAST_ACTIVE_KEY, Date.now().toString());
        lastActiveRef.current = Date.now();
        
        // Set a timeout to lock the app (only if timeout > 0)
        if (effectiveTimeout > 0) {
          visibilityTimeoutRef.current = setTimeout(() => {
            if (document.hidden && appLockEnabled) {
              setIsLocked(true);
            }
          }, effectiveTimeout);
        } else if (effectiveTimeout === 0) {
          // Immediate lock
          setIsLocked(true);
        }
      } else {
        // App coming to foreground
        clearTimeout(visibilityTimeoutRef.current);
        
        const lastActive = parseInt(localStorage.getItem(LAST_ACTIVE_KEY) || '0', 10);
        const timeSinceActive = Date.now() - lastActive;
        
        // If background time exceeded timeout, lock the app
        if (timeSinceActive > effectiveTimeout && appLockEnabled) {
          setIsLocked(true);
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      clearTimeout(visibilityTimeoutRef.current);
    };
  }, [isAuthenticated, appLockEnabled, lockOnBackground, getEffectiveTimeout]);

  // Update last active timestamp periodically when app is in use
  useEffect(() => {
    if (!isAuthenticated || isLocked) return;

    const updateLastActive = () => {
      localStorage.setItem(LAST_ACTIVE_KEY, Date.now().toString());
      lastActiveRef.current = Date.now();
    };

    // Update on user interaction
    const events = ['mousedown', 'keydown', 'touchstart', 'scroll'];
    events.forEach(event => {
      window.addEventListener(event, updateLastActive, { passive: true });
    });

    // Also update periodically
    const interval = setInterval(updateLastActive, 10000);

    return () => {
      events.forEach(event => {
        window.removeEventListener(event, updateLastActive);
      });
      clearInterval(interval);
    };
  }, [isAuthenticated, isLocked]);

  // Enable app lock
  const enableAppLock = useCallback(() => {
    localStorage.setItem(APP_LOCK_ENABLED_KEY, 'true');
    localStorage.setItem(LAST_ACTIVE_KEY, Date.now().toString());
    setAppLockEnabled(true);
  }, []);

  // Disable app lock
  const disableAppLock = useCallback(() => {
    localStorage.setItem(APP_LOCK_ENABLED_KEY, 'false');
    localStorage.removeItem('biometric_credential_id');
    localStorage.removeItem('biometric_enrolled');
    setAppLockEnabled(false);
    setIsLocked(false);
  }, []);

  // Toggle lock on background
  const toggleLockOnBackground = useCallback((enabled) => {
    localStorage.setItem(LOCK_ON_BACKGROUND_KEY, enabled.toString());
    setLockOnBackground(enabled);
  }, []);

  // Set lock timeout duration
  const setLockTimeoutDuration = useCallback((timeoutMs) => {
    localStorage.setItem(LOCK_TIMEOUT_KEY, timeoutMs.toString());
    setLockTimeout(timeoutMs);
  }, []);

  // Toggle require unlock for sensitive actions
  const toggleRequireForSensitive = useCallback((enabled) => {
    localStorage.setItem(REQUIRE_FOR_SENSITIVE_KEY, enabled.toString());
    setRequireForSensitive(enabled);
  }, []);

  // Unlock the app (called after successful biometric auth)
  const unlock = useCallback(() => {
    localStorage.setItem(LAST_ACTIVE_KEY, Date.now().toString());
    setIsLocked(false);
    
    // If there was a pending sensitive action, mark it as unlocked
    if (pendingSensitiveAction) {
      localStorage.setItem(LAST_SENSITIVE_UNLOCK_KEY, Date.now().toString());
      const callback = pendingSensitiveAction;
      setPendingSensitiveAction(null);
      // Execute the callback after a short delay to allow UI to update
      setTimeout(() => callback(), 100);
    }
  }, [pendingSensitiveAction]);

  // Lock the app manually
  const lock = useCallback(() => {
    if (appLockEnabled) {
      setIsLocked(true);
    }
  }, [appLockEnabled]);

  // Reset lock state on logout
  const resetLock = useCallback(() => {
    setIsLocked(false);
    localStorage.removeItem(LAST_ACTIVE_KEY);
    localStorage.removeItem(LAST_SENSITIVE_UNLOCK_KEY);
  }, []);

  // Check if sensitive action requires unlock
  const requiresSensitiveUnlock = useCallback(() => {
    if (!appLockEnabled || !requireForSensitive) return false;
    
    const lastUnlock = parseInt(localStorage.getItem(LAST_SENSITIVE_UNLOCK_KEY) || '0', 10);
    const timeSinceUnlock = Date.now() - lastUnlock;
    
    // If unlocked within the duration, don't require again
    return timeSinceUnlock > SENSITIVE_UNLOCK_DURATION;
  }, [appLockEnabled, requireForSensitive]);

  // Request unlock for sensitive action
  const requestSensitiveUnlock = useCallback((onSuccess) => {
    if (!requiresSensitiveUnlock()) {
      // No unlock needed, execute immediately
      onSuccess();
      return false;
    }
    
    // Store the callback and trigger lock screen
    setPendingSensitiveAction(() => onSuccess);
    setIsLocked(true);
    return true;
  }, [requiresSensitiveUnlock]);

  // Record successful sensitive action unlock
  const recordSensitiveUnlock = useCallback(() => {
    localStorage.setItem(LAST_SENSITIVE_UNLOCK_KEY, Date.now().toString());
  }, []);

  const value = {
    isLocked,
    appLockEnabled,
    lockOnBackground,
    lockTimeout,
    requireForSensitive,
    checkingLock,
    pendingSensitiveAction: !!pendingSensitiveAction,
    enableAppLock,
    disableAppLock,
    toggleLockOnBackground,
    setLockTimeoutDuration,
    toggleRequireForSensitive,
    unlock,
    lock,
    resetLock,
    requiresSensitiveUnlock,
    requestSensitiveUnlock,
    recordSensitiveUnlock,
  };

  return (
    <AppLockContext.Provider value={value}>
      {children}
    </AppLockContext.Provider>
  );
};

export default AppLockContext;
